import React, { useState, useEffect, useMemo } from 'react';
import {
  StyleSheet,
  Text,
  View,
  TextInput,
  TouchableOpacity
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { AntDesign, Ionicons } from '@expo/vector-icons';
import DataTable from "react-data-table-component";
import Modal from "react-native-modal";
import Checkbox from 'expo-checkbox';
import Data from './Data';
import { getStorage, setStorage } from '../storage';
import { useScale, ScaleFn, TablePagination , useDarkMode} from './common';

// ─── 테마 토큰 ────────────────────────────────────────
const DARK = {
  bg:        '#0F172A',
  card:      '#1E293B',
  cardInner: '#0F172A',
  border:    'rgba(99,179,237,0.12)',
  text:      '#E2E8F0',
  textSub:   '#94A3B8',
  muted:     '#a8adb497',
  trackBg:   'rgba(255,255,255,0.05)',
};
const LIGHT = {
  bg:        '#F9FAFB',
  card:      '#FFFFFF',
  cardInner: '#F3F4F6',
  border:    '#E5E7EB',
  text:      '#111827',
  textSub:   '#4B5563',
  muted:     '#9CA3AF',
  trackBg:   'rgba(0,0,0,0.03)',
};
const S = {
  accent:  '#38BDF8',
  ok:      '#34D399',
  err:     '#F87171',
  warn:    '#FB923C',
  replace: '#FBBF24',
  purple:  '#A78BFA',
  pink:    '#F472B6',
};

// ─── DeleteModal ──────────────────────────────────────
const DeleteModal = ({ deleteModal, setDeleteModal, checkedItems, setCheckedItems, getList, T }: any) => {
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const onDelete = () => {
    Data.fetchData('group/deleteGroup', {
      DATA: checkedItems
    }, (result: any, count: any) => {
      if (count > 0) {
        getList();
        setCheckedItems([]);
        alert('삭제되었습니다.');
        setDeleteModal(false);
      } else {
        alert('삭제에 실패하였습니다.');
      }
    }, true);
  };

  return (
    <Modal
      style={{ flex: 1 }}
      backdropColor="black"
      isVisible={deleteModal}
      backdropOpacity={0.7}
      onBackdropPress={() => { setDeleteModal(false); }}>
      <View style={[styles.deleteModal, { backgroundColor: T.card, borderColor: T.border }]}>
        <View style={{ justifyContent: 'center', alignItems: 'center' }}>
          <Text style={{ fontSize: s(18), fontWeight: '500', color: T.text }}>선택한 그룹을 삭제하시겠습니까?</Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: s(30) }}>
          <TouchableOpacity onPress={() => { setDeleteModal(false); }}>
            <Text style={[styles.removeButtonFont, { color: T.textSub }]}>취소</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { onDelete() }}>
            <Text style={[styles.removeButtonFont, { color: S.err }]}>삭제</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

// ─── OpenModal ────────────────────────────────────────
const OpenModal = ({ modalVisible, setModalVisible, groupData, getList, T }: any) => {
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [part, setPart] = useState('');
  const onChangePart = (text: any) => { setPart(text) };

  const onInsert = () => {
    Data.fetchData('group/insertGroup', {
      AREA: part
    }, (result: any, count: any) => {
      if (count === 1) {
        getList();
        setPart('');
        alert('등록되었습니다.');
        setModalVisible(false);
      }
    }, true);
  };

  const onRegister = () => {
    if (groupData?.length > 0) {
      const data = groupData.filter((el: any) => el?.NEW_AREA === part);
      if (data?.length > 0) {
        alert('이미 존재하는 구역입니다.');
      } else {
        onInsert();
      }
    } else {
      onInsert();
    }
  };

  return (
    <Modal
      style={{ flex: 1 }}
      backdropColor="black"
      isVisible={modalVisible}
      backdropOpacity={0.7}
      onBackdropPress={() => { setModalVisible(false); }}
    >
      <View style={[styles.modalView, { backgroundColor: T.card, borderColor: T.border }]}>
        <View style={[styles.modalHeader, { borderColor: T.border }]}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={[styles.modalTitle, { color: T.text }]}>그룹 생성</Text>
          </View>
          <TouchableOpacity onPress={() => setModalVisible(false)}>
            <AntDesign name='close' size={s(28)} color={T.textSub} />
          </TouchableOpacity>
        </View>
        <View style={styles.box}>
          <View style={styles.rowstyle}>
            <Text style={[styles.contentFont, { color: T.text }]}>담당 구역</Text>
            <View style={[styles.textInputBox, { borderColor: T.border, backgroundColor: T.cardInner }]}>
              <TextInput
                autoCapitalize='none'
                clearButtonMode='always'
                style={[styles.textInput, { color: T.text }]}
                placeholderTextColor={T.muted}
                onChangeText={(text: any) => { onChangePart(text) }}
                value={part}
              />
            </View>
          </View>
        </View>
        <View style={styles.buttonBox}>
          <TouchableOpacity style={[styles.modifyButton, { backgroundColor: S.accent }]}
            onPress={() => { onRegister() }} >
            <Text style={styles.buttonFont}>등록</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.modifyButton, { backgroundColor: T.textSub }]}
            onPress={() => { setModalVisible(false); }} >
            <Text style={styles.buttonFont}>취소</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

// ─── DeleteBox ────────────────────────────────────────
const DeleteBox = ({ setDeleteModal, T }: any) => {
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const onPressDelete = () => {
    setDeleteModal(true);
  };
  return (
    <TouchableOpacity onPress={() => { onPressDelete() }}
      style={[styles.headerBtn, { backgroundColor: S.err }]} >
      <Ionicons name="trash-outline" size={s(15)} color="#fff" />
      <Text style={styles.buttonFont}>삭제</Text>
    </TouchableOpacity>
  );
};

// ─── OnCheck ──────────────────────────────────────────
const OnCheck = ({ area, checkedItems, setCheckedItems }: any) => {
  const value = checkedItems.filter((el: any) => el?.AREA === area);

  const onChecked = (checked: any, area: any) => {
    if (checked) {
      setCheckedItems([...checkedItems, { AREA: area }]);
    } else {
      setCheckedItems(checkedItems.filter((el: any) => el?.AREA !== area));
    }
  };

  return (
    <Checkbox
      style={{ margin: 3 }}
      color={S.accent}
      value={value?.length > 0}
      onChange={(e: any) => onChecked(e?.target?.checked, area)}
    />
  );
};

// ─── OnAllChecked ─────────────────────────────────────
const OnAllChecked = ({ checkedItems, setCheckedItems, groupData }: any) => {
  const onChecked = (checked: any) => {
    if (checked) {
      const checkedList: any[] = [];
      groupData.forEach((el: any) => checkedList.push({ AREA: el?.AREA }));
      setCheckedItems(checkedList);
    } else {
      setCheckedItems([]);
    }
  };

  return (
    <Checkbox
      style={{ margin: 3 }}
      color={S.accent}
      value={checkedItems?.length > 0 && checkedItems?.length === groupData?.length}
      onChange={(e: any) => onChecked(e?.target?.checked)}
    />
  );
};

// ─── Groups (main) ────────────────────────────────────
export default function Groups({ navigation }: any) {
  const isFocused = useIsFocused();
  const { scale, s } = useScale();
  const styles = useMemo(() => makeStyles(s), [scale]);
  const [modalVisible, setModalVisible] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [groupData, setGroupData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const _totalPages = Math.max(1, Math.ceil(((groupData) || []).length / perPage));
  const _curPage = Math.min(Math.max(1, page), _totalPages);
  const pageData = ((groupData) || []).slice((_curPage - 1) * perPage, _curPage * perPage);
  const [checkedItems, setCheckedItems] = useState<any>([]);
  const [admin, setAdmin] = useState('1');
  const { dark, setDark } = useDarkMode();

  useEffect(() => {
    if (!isFocused) return;
    getStorage('darkMode').then((v: any) => { if (v !== null) setDark(v); });
  }, [isFocused]);

  const T = dark ? DARK : LIGHT;

  const tableStyles = {
    headRow:    { style: { backgroundColor: T.cardInner, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(40) } },
    headCells:  { style: { color: T.textSub, fontSize: s(12), fontWeight: '600' as any, paddingLeft: s(12), paddingRight: s(12) } },
    rows:       { style: { backgroundColor: T.card, color: T.text, borderBottomColor: (dark ? 'rgba(148,163,184,0.30)' : '#CBD5E1'), borderBottomWidth: 1, minHeight: s(44), '&:hover': { backgroundColor: T.cardInner } } },
    cells:      { style: { color: T.text, fontSize: s(13), paddingLeft: s(12), paddingRight: s(12) } },
    pagination: { style: { backgroundColor: T.card, color: T.textSub, borderTopColor: T.border, borderTopWidth: 1, minHeight: s(44) },
      pageButtonsStyle: { color: T.textSub, fill: T.textSub, '&:disabled': { color: T.muted, fill: T.muted }, '&:hover:not(:disabled)': { backgroundColor: T.cardInner } } },
    noData:     { style: { backgroundColor: T.card, color: T.muted, padding: s(30) } },
  };

  const groupColumns = [
    {
      id: 1,
      name: <OnAllChecked checkedItems={checkedItems} setCheckedItems={setCheckedItems} groupData={groupData} />,
      cell: (row: any, index: any) => <OnCheck area={row?.AREA} checkedItems={checkedItems} setCheckedItems={setCheckedItems} />,
      grow: 0,
    },
    {
      id: 2,
      name: "구역",
      selector: (row: any) => row.AREA,
    },
    {
      id: 3,
      name: '담당자',
      selector: (row: any) => row.NAME,
    },
    {
      id: 4,
      name: '담당자 전화번호',
      selector: (row: any) => row.PHONE,
    },
    {
      id: 5,
      name: '아이디',
      selector: (row: any) => row.ID,
    },
    {
      id: 6,
      name: '이메일',
      selector: (row: any) => row.EMAIL,
    },
  ];

  const getList = async () => {
    const userInfo = await getStorage('AutoLogin');
    const adminValue = userInfo?.admin ?? '1';
    setAdmin(adminValue);

    const endpoint = adminValue === '0'
      ? 'group/getGroupList'
      : 'group/getGroupListbyUser';

    Data.fetchData(endpoint, {}, (result: any, count: any) => {
      if (count > 0) {
        const sorted = result.sort((a: any, b: any) => {
          if (a.AREA === '로밍') return 1;
          if (b.AREA === '로밍') return -1;
          return a.AREA.localeCompare(b.AREA, 'ko');
        });
        setGroupData(sorted);
      } else {
        setGroupData([]);
      }
    }, true);
  };

  useEffect(() => {
    if (!isFocused) return;
    getList();
  }, [isFocused]);

  return (
    <View style={[styles.container, { backgroundColor: T.bg }]}>
      <View style={[styles.dataTable, { backgroundColor: T.card, borderColor: T.border, shadowColor: dark ? 'transparent' : '#d9dbec' }]}>
        {/* ─── 헤더 ─── */}
        <View style={styles.TopBox}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
            <Text style={{ fontSize: s(18), fontWeight: '700', color: T.text }}>구역 관리</Text>
            {groupData?.length > 0 && (
              <View style={{ backgroundColor: S.accent + '18', paddingHorizontal: s(8), paddingVertical: s(2), borderRadius: s(10) }}>
                <Text style={{ fontSize: s(12), fontWeight: '700', color: S.accent }}>{groupData.length}</Text>
              </View>
            )}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: s(10) }}>
            {admin === '0' && (
              <TouchableOpacity style={[styles.headerBtn, { backgroundColor: S.accent }]}
                onPress={() => setModalVisible(true)}>
                <Ionicons name="add-circle-outline" size={s(15)} color="#fff" />
                <Text style={styles.buttonFont}>그룹 생성</Text>
              </TouchableOpacity>
            )}
            {admin === '0' && <DeleteBox setDeleteModal={setDeleteModal} T={T} />}
            <TouchableOpacity
              style={[styles.iconBtn, { backgroundColor: dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' }]}
              onPress={() => { navigation.navigate('Setting') }}
            >
              <Ionicons name="settings-sharp" size={s(16)} color={T.textSub} />
            </TouchableOpacity>
          </View>
        </View>
        {/* ─── 테이블 ─── */}
        <DataTable
          columns={groupColumns}
          data={pageData}
          customStyles={tableStyles}
          noDataComponent={
            <View style={{ padding: s(40), alignItems: 'center', justifyContent: 'center' }}>
              <Text style={{ fontSize: s(16), color: T.muted }}>
                등록된 구역이 없습니다.
              </Text>
            </View>
          }
          onRowClicked={(row: any) => {
            try { window.localStorage.setItem(`_navParams_GroupDetail_${row.AREA}`, JSON.stringify({ data: row })); } catch {}
            navigation.navigate('GroupDetail', { area: row.AREA });
          }}
        />
        {(groupData || []).length > 0 && (
          <TablePagination
            rowCount={(groupData || []).length}
            perPage={perPage}
            page={_curPage}
            onChangePage={setPage}
            onChangePerPage={(n) => { setPerPage(n); setPage(1); }}
            T={T}
            s={s}
            accent={S.accent}
          />
        )}
      </View>
      <DeleteModal
        deleteModal={deleteModal}
        setDeleteModal={setDeleteModal}
        checkedItems={checkedItems}
        setCheckedItems={setCheckedItems}
        getList={getList}
        T={T}
      />
      <OpenModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
        groupData={groupData}
        getList={getList}
        T={T}
      />
    </View>
  );
}

const makeStyles = (s: ScaleFn) => StyleSheet.create({
  container: {
    flex: 1,
    padding: s(24),
  },
  dataTable: {
    borderRadius: s(16),
    borderWidth: 0.5,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: s(20),
    elevation: 3,
    paddingTop: s(20),
    paddingHorizontal: s(10),
  },
  TopBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: s(6),
    paddingBottom: s(12),
  },
  iconBtn: {
    width: s(34),
    height: s(34),
    borderRadius: s(10),
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: s(5),
    paddingHorizontal: s(14),
    paddingVertical: s(7),
    borderRadius: s(10),
  },
  buttonBox: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'center',
  },
  modifyButton: {
    paddingHorizontal: s(20),
    paddingVertical: s(6),
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 0,
    borderRadius: s(10),
    marginHorizontal: s(20),
  },
  buttonFont: {
    fontSize: s(14),
    fontWeight: '600',
    color: '#fff',
  },
  modalView: {
    margin: s(20),
    borderWidth: 1,
    borderRadius: s(20),
    padding: s(35),
    alignSelf: 'center',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: s(4),
    elevation: 5,
    width: '45%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: s(10),
    marginBottom: s(10),
    borderBottomWidth: 3,
  },
  modalTitle: {
    fontSize: s(22),
    fontWeight: '700',
    textAlign: 'center',
    marginRight: s(20),
  },
  box: {
    width: '100%',
    borderWidth: 0,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: s(30),
  },
  rowstyle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: s(5),
    borderWidth: 0,
    justifyContent: 'space-around',
  },
  contentFont: {
    fontSize: s(20),
    fontWeight: '600',
  },
  textInputBox: {
    borderWidth: 1,
    borderRadius: s(5),
    width: '60%',
    height: s(40),
    justifyContent: 'center',
    alignItems: 'center',
  },
  textInput: {
    fontSize: s(18),
    width: '100%',
    height: '100%',
    paddingHorizontal: s(10),
  },
  deleteModal: {
    borderWidth: 1,
    borderRadius: s(20),
    padding: s(30),
    alignSelf: 'center',
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: s(4),
    elevation: 5,
    width: '35%',
  },
  removeButtonFont: {
    fontSize: s(16),
    fontWeight: 'bold',
    marginHorizontal: s(20),
  },
});
